import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CompanyDetailContainer } from 'src/components/Company/CompanyDetailContainer'
import { CompanyHeader } from 'src/components/CompanyHeader/CompanyHeader'
import { Footer } from 'src/components/Footer/Footer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { Section } from 'src/components/global/Overview/Section'
import { SearchField } from 'src/components/global/SearchField'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
import { SearchPagination } from 'src/components/SearchPagination/SearchPagination'
import { SolutionBreadcrumb } from 'src/components/SolutionBreadcrumb/SolutionBreadcrumb'
import { SolutionSearchPageTab } from 'src/components/Tabs/SolutionSearchPageTab'
import { THEME } from 'src/config/config'
import { FUNDING_DETAILS, fundingAmountsC, fundingInfosC, OPEN_TO_INVESTMENT } from 'src/constants/constants'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { ICompany } from 'src/graphql/generated/hooks'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { AppState } from 'src/store/types'
import { Main } from 'src/templates/Main'
import { COMPANIES_DETAILS_TAB, COMPANIES_DIRECTORY_TAB, FeaturesType, FilterOption, SectionItem } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'
import { getYearFoundedItem } from 'src/utils/getYearFoundedItem'
import { withRetries } from 'src/utils/retryHelper'

type Props = {
  company: ICompany
  filters: FilterOption[]
}

const CompanyPage: NextPage<Props> = ({ company, ...props }: Props) => {
  const router = useRouter()
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const { updateQueryParams } = useUpdateQueryParams()
  const tabs = router.query.tabs
  const pathName = tabs ? tabs[0] : 'Details'
  const defaultTabIdx = pathName === 'Details' ? 401 : pathName === 'Directory' ? 402 : 401
  const [tabIdx, setTabIdx] = useState(defaultTabIdx)
  const overviewItems: SectionItem[] = []
  const randomNum = Math.random().toString(36).substring(2, 15) // Generate a random string

  const name = company?.name || ''
  const description = company?.description || ''
  const imageUrl = useDefaultLogo(company?.logo?.url)
  const website = company?.website || ''
  const hqs = company?.hqs || []
  const regionsServed = company?.regionsServed || []
  const yearFounded = company?.yearFounded || ''
  const size = company?.size || ''
  const services = company?.services || ''
  const linkedin = company?.linkedin || ''
  const twitter = company?.twitter || ''
  const fundingInfo = company?.fundingInfo || ''
  const fundingAmount = company?.fundingAmount || ''
  const fundingDetails = company?.fundingDetails || ''
  const openToInvestment = company?.openToInvestment || false
  const companyType = authInfo?.companyType || ''
  const isPremiumUser = authInfo?.isPremium || false
  let showOpenToInvestment = false
  const consolidationData = company?.consolidationData || null
  const showWebsite = consolidationData?.enableInActiveDir === false ? false : true

  if (companyType && companyType.toLocaleLowerCase() === 'investor') {
    showOpenToInvestment = true
  }

  useEffect(() => {
    const { tabs } = router.query
    if (tabs && tabs === 'Directory') {
      setTabIdx(COMPANIES_DIRECTORY_TAB)
    }
  }, [router])

  if (checkNotEmpty(hqs)) {
    const convertedItems: FeaturesType[] =
      (hqs &&
        hqs?.map((item: any) => {
          const converted: FeaturesType = { name: item?.name || '', iconColor: '#011D58' }
          return converted
        })) ||
      []

    const item: SectionItem = {
      title: 'HEADQUARTERS',
      type: 'SpanDataIcon',
      subType: 'normal',
      items: convertedItems,
    }
    overviewItems.push(item)
  }

  if (checkNotEmpty(regionsServed)) {
    const convertedItems: FeaturesType[] =
      (regionsServed &&
        regionsServed?.map((item: any) => {
          const converted: FeaturesType = { name: item?.name || '' }
          return converted
        })) ||
      []

    const item: SectionItem = {
      title: 'Regions Served',
      type: 'SpanDataList',
      subType: 'normal',
      items: convertedItems,
    }
    overviewItems.push(item)
  }
  if (checkNotEmpty(yearFounded)) {
    try {
      let year: number | undefined
      if (typeof yearFounded === 'string' || yearFounded instanceof String) {
        const dateObj = new Date(yearFounded as string)
        year = dateObj.getFullYear()
      } else if (typeof yearFounded === 'number') {
        year = yearFounded as number
      }
      const yearFoundedItem = getYearFoundedItem('Year founded', year)
      if (yearFoundedItem) {
        overviewItems.push(yearFoundedItem)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing yearFounded:', error)
    }
  }
  if (checkNotEmpty(size)) {
    const item: SectionItem = { title: 'Size', type: 'SpanDataList', subType: 'normal', items: [{ name: size }] }
    overviewItems.push(item)
  }

  if (THEME === 'legaltech' && checkNotEmpty(linkedin)) {
    const displayName = company?.name || 'Company'
    const title = `View ${displayName} on LinkedIn`
    const item: SectionItem = { type: 'LinkedinIconLink', items: [linkedin!], title }
    overviewItems.push(item)
  }

  if (THEME === 'legaltech' && checkNotEmpty(twitter)) {
    const displayName = company?.name || 'Company'
    const title = `View ${displayName} on Twitter`
    const item: SectionItem = { type: 'TwitterIconLink', items: [twitter!], title }
    overviewItems.push(item)
  }

  if (THEME === 'legaltech' && checkNotEmpty(fundingInfo)) {
    const item: SectionItem = {
      title: 'Latest Funding Round',
      type: 'DottedDataList',
      subType: 'more',
      isNewLine: true,
      items: [{ name: fundingInfosC.find((item: any) => item.value === fundingInfo)?.label }],
    }
    overviewItems.push(item)
  }

  if (THEME === 'legaltech' && checkNotEmpty(fundingAmount)) {
    const item: SectionItem = {
      title: 'Total Funding Amount',
      type: 'DottedDataList',
      subType: 'more',
      items: [{ name: fundingAmountsC.find((item: any) => item.value === fundingAmount)?.label }],
    }
    overviewItems.push(item)
  }

  if (THEME === 'legaltech' && checkNotEmpty(fundingDetails)) {
    const item: SectionItem = {
      title: FUNDING_DETAILS,
      type: 'string',
      subType: '',
      items: [],
      sValue: fundingDetails || '',
      isFullWidth: true,
    }
    overviewItems.push(item)
  }

  if (
    THEME === 'legaltech' &&
    showOpenToInvestment &&
    openToInvestment &&
    checkNotEmpty(openToInvestment) &&
    isPremiumUser
  ) {
    const item: SectionItem = {
      title: 'OPEN_TO_INVESTMENT',
      type: 'CheckDataList',

      items: [{ name: OPEN_TO_INVESTMENT, isChecked: true }],
    }
    overviewItems.push(item)
  }

  const onSelectTab = (index: number) => {
    setTabIdx(index)
    let newTabs = 'Details'
    if (index === COMPANIES_DETAILS_TAB) {
      newTabs = 'Details'
    }
    if (index === COMPANIES_DIRECTORY_TAB) {
      newTabs = 'Directory'
    }

    const keyword = router.query.keyword

    updateQueryParams({
      tabs: newTabs,
      keyword: (keyword as string | undefined) || undefined,
    })
  }

  if (!name || !description || !website) {
    return <div></div>
  }

  const meta = <Meta title={name} description={description} url={`/companies/${company?.slug}/`} image={imageUrl} />
  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <ThemeOnly theme="ilta">
          <IltaHeader />
        </ThemeOnly>
        <div className="flex flex-col bg-clouds">
          {company && (
            <>
              <SolutionBreadcrumb companyName={company?.name || ''} />
              <CompanyHeader company={company} />
            </>
          )}
        </div>
        <div className="flex w-full bg-clouds justify-center items-center">
          <div className="!max-w-[1100px] w-full flex flex-row justify-start items-center">
            <SolutionSearchPageTab
              id={COMPANIES_DETAILS_TAB}
              activeTab={tabIdx}
              className="w-[117px]"
              onSelectTab={onSelectTab}
            >
              {THEME === 'ilta' ? 'Details' : 'Overview'}
            </SolutionSearchPageTab>
            <SolutionSearchPageTab
              id={COMPANIES_DIRECTORY_TAB}
              activeTab={tabIdx}
              className="w-[117px]"
              onSelectTab={onSelectTab}
            >
              {THEME === 'ilta' ? 'Directory' : 'Solutions'}
            </SolutionSearchPageTab>
          </div>
        </div>
        {tabIdx === COMPANIES_DETAILS_TAB && (
          <div className="flex flex-col">
            <div className="flex justify-center">
              <div className="!max-w-[1100px] flex flex-col justify-between w-full md:mb-32">
                <Section
                  title={THEME === 'ilta' ? 'Overview' : 'Summary'}
                  description={description || ''}
                  url={!showWebsite ? '' : website || ''}
                  items={overviewItems}
                  isFromCompany={true}
                />
                <ThemeOnly theme="legaltech">
                  <CompanyDetailContainer
                    key={`${randomNum}-CompanyDetailContainer`}
                    company={company}
                    isFromCompany={true}
                  />
                </ThemeOnly>
                <Section title={'Services'} description={''} url={''} items={[]} content={services} />
              </div>
            </div>
          </div>
        )}

        {tabIdx === COMPANIES_DIRECTORY_TAB && (
          <div className="flex flex-col">
            <div className="flex justify-center">
              <div className="!max-w-[1100px] flex flex-col justify-between w-full md:mb-32 relative">
                <div className="w-1/4 pr-10 flex flex-row">
                  <SearchField className="my-8" />
                </div>
                {company && (
                  <SearchPagination
                    filterProps={props.filters}
                    companyId={company?.documentId || ''}
                    companyName={name}
                    followOnly={false}
                    includeCompany={false}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default CompanyPage

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    if (!params?.slug) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }
    const slug = params.slug as string
    const [{ companies }] = await Promise.all([
      withRetries(() => apolloSdk.getCompanyBySlug({ slug, includeIlta: THEME === 'ilta' })),
    ])
    if (!companies || companies?.length === 0) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }
    const filters: object[] = []
    return {
      props: {
        company: companies?.[0] || null,
        filters,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching company:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { companies } = await apolloSdk.getAllCompanies()
  const paths: Array<{ params: { slug: string } }> = []

  if (companies) {
    companies.forEach((company: any) => {
      if (!company?.slug) return
      paths.push({ params: { slug: company?.slug } })
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}
